import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Autocomplete, TextField, CircularProgress, Checkbox, Typography, InputLabel, Box } from "@mui/material";
import { debounce } from "lodash";
import { GlobalState } from "context/GlobalContext";
import { constraintsOptions } from "components/PimDashboard/data";
import { inputTypes } from "data/globalData";
import { grey } from "@mui/material/colors";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { LightTooltip } from "../CustomToolTip/CustomToolTip";
import { fetchMasterDefaultOnce } from "components/PimDashboard/DashboardComponents/Sourcing/shared/bqMasterDropdownCache";

const SELECT_ALL = { key: "__select_all__", name: "Select All" };

/**
 * storeValueAs: "key" | "object"
 * - "key": single -> string, multiple -> string[]
 * - "object": single -> object, multiple -> object[]
 */
const SearchInputFieldWithDebounce = ({
	column,
	data,
	setData,
	fetchData,
	selectOptKeyName,
	disableLabel = false,
	width = "100%",
	storeValueAs = "key", // "key" | "object"
	disabled = false,
	sx = {},
	inputSx = {},
	labelSx = {},
	error = false,
	onChange,
	cacheKey,
	placeholder,
	/** MUI outlined floating label on the TextField (disables external InputLabel). */
	floatingLabel = false,
	/** When true, skip MUI client-side filtering (use for async/server search). */
	disableClientFilter = false,
	/** Shown when the saved code is not in the loaded options yet. */
	selectedLabel,
}) => {
	const { snackBar, setSnackBar, setSnackBarOpen, setDropdownDataCache, getDropdownCache } = GlobalState();

	const required = !!column?.constraints?.[constraintsOptions.NOTNULL.key];
	const resolvedCacheKey = cacheKey ?? column.key;
	const columnKey = column?.key;
	const fetchDataRef = useRef(fetchData);
	const skipSearchRef = useRef(false);
	const columnRef = useRef(column);
	const snackBarRef = useRef(snackBar);
	const getDropdownCacheRef = useRef(getDropdownCache);
	const setDropdownDataCacheRef = useRef(setDropdownDataCache);

	useEffect(() => {
		fetchDataRef.current = fetchData;
	}, [fetchData]);

	useEffect(() => {
		columnRef.current = column;
	}, [column]);

	useEffect(() => {
		snackBarRef.current = snackBar;
	}, [snackBar]);

	useEffect(() => {
		getDropdownCacheRef.current = getDropdownCache;
		setDropdownDataCacheRef.current = setDropdownDataCache;
	}, [getDropdownCache, setDropdownDataCache]);

	const [query, setQuery] = useState("");
	const [loading, setLoading] = useState(false);
	const [optionsList, setOptionsList] = useState([]);
	const [defaultReadyKey, setDefaultReadyKey] = useState("");
	const ensuredSelectionRef = useRef("");

	const multiple = column.input_type === inputTypes.MULTIPLE_SELECT.key;

	// raw value from parent data (may be key or object depending on previous saves)
	const rawValue = data?.[column.key];

	// Normalize option items (merge any label info from column[selectOptKeyName].getOptionLabel)
	const normalize = (list = []) => {
		return list.map((opt) => {
			if (typeof column?.[selectOptKeyName]?.getOptionLabel === "function") {
				// If user returns extra label props from their helper, merge them
				return { ...opt, ...column[selectOptKeyName]?.getOptionLabel(opt) };
			} else {
				return opt;
			}
		});
	};

	const removeDuplicates = (list = []) =>
		Object.values(
			list.reduce((acc, row) => {
				if (row.key !== SELECT_ALL.key) acc[String(row.key)] = row;
				return acc;
			}, {}),
		);

	const sameKey = (a, b) => a != null && b != null && String(a) === String(b);

	/** Saved edit values are codes. Search that code so the option is loaded even when it is outside the first page. */
	const selectedSearchTerm = (raw) => {
		if (raw == null || raw === "" || Array.isArray(raw)) return "";
		if (typeof raw === "object") {
			const key = raw.key ?? raw.code;
			return key == null || key === "" ? "" : String(key);
		}
		return String(raw);
	};

	const normalizeOptions = (list = []) =>
		(list || []).map((opt) => {
			const col = columnRef.current;
			if (typeof col?.[selectOptKeyName]?.getOptionLabel === "function") {
				return { ...opt, ...col[selectOptKeyName].getOptionLabel(opt) };
			}
			return opt;
		});

	// Load each master once on mount (stable deps — avoid refetch when GlobalContext cache updates).
	useEffect(() => {
		let cancelled = false;
		setDefaultReadyKey("");
		setOptionsList([]);

		const run = async () => {
			const cached = getDropdownCacheRef.current(resolvedCacheKey);
			if (Array.isArray(cached) && cached.length > 0) {
				if (!cancelled) {
					setOptionsList(multiple ? [SELECT_ALL, ...cached] : cached);
					setDefaultReadyKey(resolvedCacheKey);
				}
				return;
			}

			if (typeof fetchDataRef.current !== "function") {
				if (!cancelled) {
					setOptionsList([]);
					setDefaultReadyKey(resolvedCacheKey);
				}
				return;
			}

			if (!cancelled) setLoading(true);
			try {
				const res = await fetchMasterDefaultOnce(resolvedCacheKey, () => fetchDataRef.current());
				if (cancelled) return;
				const normalized = normalizeOptions(res?.data?.result ?? []);
				setOptionsList(multiple ? [SELECT_ALL, ...normalized] : normalized);
				if (normalized.length > 0) {
					setDropdownDataCacheRef.current(resolvedCacheKey, normalized);
				}
			} catch (err) {
				if (cancelled) return;
				setSnackBar({
					...snackBarRef.current,
					message: parseErrorMessage(err),
					severity: "error",
				});
				setSnackBarOpen(true);
			} finally {
				if (!cancelled) {
					setLoading(false);
					setDefaultReadyKey(resolvedCacheKey);
				}
			}
		};

		run();
		return () => {
			cancelled = true;
		};
	}, [columnKey, resolvedCacheKey, multiple, selectOptKeyName, setSnackBar, setSnackBarOpen]);

	// A saved value may sit outside the first page. Search it only after that page is loaded, and keep the hit on this field.
	useEffect(() => {
		if (multiple || defaultReadyKey !== resolvedCacheKey) return undefined;
		const term = selectedSearchTerm(rawValue);
		if (!term || typeof fetchDataRef.current !== "function") return undefined;
		const selectedKey = typeof rawValue === "object" ? rawValue.key ?? rawValue.code : rawValue;
		const alreadyLoaded = optionsList.some((opt) => sameKey(opt.key, selectedKey));
		const ensureKey = `${resolvedCacheKey}:${term}`;
		if (alreadyLoaded) {
			ensuredSelectionRef.current = ensureKey;
			return undefined;
		}
		if (ensuredSelectionRef.current === ensureKey) return undefined;

		let cancelled = false;
		const run = async () => {
			setLoading(true);
			try {
				const res = await fetchDataRef.current(0, 20, term);
				if (cancelled) return;
				const normalized = normalizeOptions(res?.data?.result ?? []);
				ensuredSelectionRef.current = ensureKey;
				if (!normalized.length) return;
				setOptionsList((prev) => {
					const prevOptions = prev.filter((row) => row.key !== SELECT_ALL.key);
					return removeDuplicates([...normalized, ...prevOptions]);
				});
			} catch (err) {
				if (!cancelled) ensuredSelectionRef.current = "";
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		run();
		return () => {
			cancelled = true;
		};
	}, [defaultReadyKey, rawValue, optionsList, resolvedCacheKey, multiple, selectOptKeyName]);

	const fetchSuggestions = useCallback(
		async (searchTerm) => {
			if (!searchTerm.trim()) return;
			setLoading(true);
			try {
				const res = await fetchDataRef.current(0, 20, searchTerm);
				const result = res?.data?.result ?? [];
				const normalized = (result ?? []).map((opt) => {
					if (typeof column?.[selectOptKeyName]?.getOptionLabel === "function") {
						return { ...opt, ...column[selectOptKeyName].getOptionLabel(opt) };
					}
					return opt;
				});
				setOptionsList((prev) =>
					multiple
						? [SELECT_ALL, ...removeDuplicates([...normalized, ...prev.filter((row) => row.key !== SELECT_ALL.key)])]
						: removeDuplicates([...normalized, ...prev.filter((row) => row.key !== SELECT_ALL.key)]),
				);
			} catch (error) {
				console.error("Error fetching suggestions:", error);
			} finally {
				setLoading(false);
			}
		},
		[column, multiple, selectOptKeyName],
	);

	const debouncedFetch = useMemo(() => debounce(fetchSuggestions, 800), [fetchSuggestions]);

	useEffect(() => {
		return () => debouncedFetch.cancel();
	}, [debouncedFetch]);

	const getLabel = (option) => {
		if (!option) return "";
		if (typeof option === "string") return option;
		return option.name ?? String(option.key ?? "");
	};

	// helpers
	const effectiveOptions = optionsList.filter((o) => o.key !== SELECT_ALL.key);
	const optionFromKey = (k) => optionsList.find((o) => sameKey(o.key, k));
	const fallbackName = (raw) => (selectedLabel ? String(selectedLabel) : String(raw));

	// compute selected keys array (for select-all logic)
	const selectedKeysArray = useMemo(() => {
		if (!multiple) return [];
		if (!rawValue) return [];
		if (storeValueAs === "object") {
			if (!Array.isArray(rawValue)) return [];
			return rawValue.map((v) => (typeof v === "object" ? v.key : v));
		} else {
			// key mode
			return Array.isArray(rawValue) ? rawValue : [];
		}
	}, [rawValue, multiple, storeValueAs]);

	const isAllSelected =
		multiple &&
		effectiveOptions.length > 0 &&
		selectedKeysArray.length > 0 &&
		selectedKeysArray.length === effectiveOptions.length;

	// Map parent's rawValue to Autocomplete's value prop
	const value = useMemo(() => {
		if (!multiple) {
			// single select
			if (rawValue == null || rawValue === "") return null;

			if (storeValueAs === "object") {
				// rawValue might be an object or a key (if previous state was different)
				if (typeof rawValue === "object" && rawValue.key !== undefined) {
					// prefer the object from optionsList (so references match MUI), fall back to rawValue
					const found = optionsList.find((o) => sameKey(o.key, rawValue.key));
					return found ?? rawValue;
				} else {
					// raw value is a primitive key
					return optionFromKey(rawValue) ?? { key: rawValue, name: fallbackName(rawValue) };
				}
			} else {
				// storeValueAs === "key" — keep the looked-up name visible until the option arrives
				return optionFromKey(rawValue) ?? { key: rawValue, name: fallbackName(rawValue) };
			}
		} else {
			// multiple
			if (!Array.isArray(rawValue)) return [];

			if (storeValueAs === "object") {
				// rawValue may contain objects and/or keys; map to option objects where possible
				return rawValue.map((v) => {
					if (typeof v === "object" && v.key !== undefined) {
						return optionsList.find((o) => sameKey(o.key, v.key)) ?? v;
					} else {
						return optionFromKey(v) ?? { key: v, name: String(v) };
					}
				});
			} else {
				// key mode -> return filtered option objects matching stored keys
				return optionsList.filter((opt) => rawValue.includes(opt.key));
			}
		}
	}, [rawValue, optionsList, multiple, storeValueAs, optionFromKey, selectedLabel]);

	useEffect(() => {
		if (multiple) {
			if (!Array.isArray(value) || value.length === 0) setQuery("");
			return;
		}

		if (!value) {
			// Don't leave skipSearch stuck true — that swallows the first keystroke / paste.
			skipSearchRef.current = false;
			setQuery("");
			return;
		}

		skipSearchRef.current = true;
		setQuery(getLabel(value));
	}, [value, multiple]);

	const selectedSingleLabel = !multiple && value ? getLabel(value) : "";

	useEffect(() => {
		if (!query.trim()) return;
		if (skipSearchRef.current) {
			skipSearchRef.current = false;
			return;
		}
		// Selection syncs the input to the chosen label — do not treat that as a new search.
		if (!multiple && selectedSingleLabel && query === selectedSingleLabel) return;
		debouncedFetch(query);
	}, [query, debouncedFetch, multiple, selectedSingleLabel]);

	// improved equality checker (handles string keys, objects, arrays)
	const isOptionEqualToValue = (opt, val) => {
		if (!opt) return false;
		if (val == null) return false;

		// primitive (string/number) value
		if (typeof val === "string" || typeof val === "number") return sameKey(opt.key, val);

		// array (multiple's internal value sometimes)
		if (Array.isArray(val)) {
			return val.some((v) => {
				if (!v) return false;
				if (typeof v === "string" || typeof v === "number") return sameKey(opt.key, v);
				return sameKey(opt.key, v.key ?? v);
			});
		}

		// object
		if (typeof val === "object") return sameKey(opt.key, val.key ?? val);

		return false;
	};

	// when user changes selection
	const handleChange = (event, newValue) => {
		debouncedFetch.cancel();
		skipSearchRef.current = true;
		let finalValue;

		// SINGLE SELECT
		if (!multiple) {
			setQuery(newValue ? getLabel(newValue) : "");
			finalValue = storeValueAs === "object" ? (newValue ?? null) : (newValue?.key ?? "");

			if (typeof onChange === "function") {
				onChange({ target: { value: finalValue } });
			} else {
				setData((prev) => ({ ...prev, [column.key]: finalValue }));
			}
			return;
		}

		// MULTIPLE SELECT
		const lastSelected = newValue[newValue.length - 1];
		if (lastSelected?.key === SELECT_ALL.key) {
			if (storeValueAs === "object") {
				finalValue = isAllSelected ? [] : effectiveOptions.map((o) => o);
			} else {
				finalValue = isAllSelected ? [] : effectiveOptions.map((o) => o.key);
			}
			setQuery("");

			if (typeof onChange === "function") {
				onChange({ target: { value: finalValue } });
			} else {
				setData((prev) => ({ ...prev, [column.key]: finalValue }));
			}
			return;
		}

		const filtered = newValue.filter((opt) => opt.key !== SELECT_ALL.key);
		if (storeValueAs === "object") {
			finalValue = filtered.map((opt) => optionsList.find((o) => o.key === opt.key) ?? opt);
		} else {
			finalValue = filtered.map((opt) => opt.key);
		}
		setQuery("");

		if (typeof onChange === "function") {
			onChange({ target: { value: finalValue } });
		} else {
			setData((prev) => ({ ...prev, [column.key]: finalValue }));
		}
	};

	const showExternalLabel = !disableLabel && !floatingLabel;
	const hasValue = multiple ? Array.isArray(value) && value.length > 0 : value != null && value !== "";
	const fieldLabel = floatingLabel ? column?.name : undefined;

	return (
		<Box sx={{ ...sx, width }}>
			{showExternalLabel && (
				<LightTooltip title={column?.name + (required ? "*" : "")} placement="bottom-start" arrow>
					<InputLabel
						sx={{
							overflow: "hidden",
							color: grey[600],
							...labelSx,
						}}>
						{column?.name} {required && <span style={{ color: "red" }}>*</span>}
					</InputLabel>
				</LightTooltip>
			)}

			<Autocomplete
				multiple={multiple}
				disabled={disabled}
				disableCloseOnSelect={multiple}
				options={optionsList}
				loading={loading}
				value={value}
				inputValue={query}
				onChange={handleChange}
				onInputChange={(event, newInputValue, reason) => {
					// user typed
					if (reason === "input") {
						setQuery(newInputValue);
					} else if (reason === "clear") {
						setQuery("");
					}
				}}
				filterOptions={disableClientFilter ? (opts) => opts : undefined}
				getOptionLabel={(option) => getLabel(option)}
				isOptionEqualToValue={isOptionEqualToValue}
				renderTags={(val) => (
					<span
						style={{
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}>
						{Array.isArray(val) ? val.map((opt) => getLabel(opt)).join(", ") : getLabel(val)}
					</span>
				)}
				renderOption={(props, option, { selected }) => {
					const isSelectAll = option.key === SELECT_ALL.key;
					const isIndeterminate =
						isSelectAll &&
						Array.isArray(selectedKeysArray) &&
						selectedKeysArray.length > 0 &&
						selectedKeysArray.length < effectiveOptions.length;
					const isChecked = isSelectAll ? isAllSelected : selected;

					return (
						<li {...props} key={option.key}>
							{multiple && (
								<Checkbox
									checked={Boolean(isChecked)}
									indeterminate={Boolean(isIndeterminate)}
									sx={{ margin: 0, padding: "2px", mr: 1 }}
									disableRipple
								/>
							)}
							<Typography color={isSelectAll ? "green" : "inherit"}>{option.name}</Typography>
						</li>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						label={fieldLabel}
						required={floatingLabel ? required : undefined}
						placeholder={floatingLabel ? undefined : placeholder || `Search ${column?.name}`}
						variant="outlined"
						size="small"
						fullWidth
						error={!!error}
						helperText={typeof error === "string" ? error : ""}
						InputLabelProps={
							floatingLabel
								? { shrink: hasValue || Boolean(query) || undefined, ...params.InputLabelProps }
								: params.InputLabelProps
						}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{loading && <CircularProgress size={20} />}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				)}
				sx={{
					...inputSx,
					"& .MuiAutocomplete-inputRoot": {
						display: "flex",
						alignItems: "center",
						flexWrap: "nowrap",
						height: floatingLabel ? undefined : "40px",
						minHeight: "40px",
						overflow: floatingLabel ? "visible" : "hidden",
						width: "100%",
						...inputSx?.["& .MuiAutocomplete-inputRoot"],
					},
					"& .MuiAutocomplete-tag": {
						flex: Array.isArray(value) && value.length ? "1 1 100%" : "0 0 0%",
						width: Array.isArray(value) && value.length ? "100%" : "0%",
						overflow: "hidden",
						whiteSpace: "nowrap",
						textOverflow: "ellipsis",
						marginRight: "4px",
						transition: "all 0.3s ease",
						display: Array.isArray(value) && value.length ? "flex" : "none",
					},
					"& .MuiAutocomplete-input": {
						flex: Array.isArray(value) && value.length ? "0 0 0%" : "1 1 100%",
						minWidth: 0,
						width: Array.isArray(value) && value.length ? "1px" : "100%",
						pointerEvents: Array.isArray(value) && value.length ? "none" : "auto",
						transition: "all 0.3s ease",
						display: Array.isArray(value) && value.length ? "none" : "block",
						padding: 0,
					},
					"&.Mui-focused .MuiAutocomplete-input, &:hover .MuiAutocomplete-input":
						Array.isArray(value) && value.length
							? {
									flex: "0 0 40%",
									width: "auto",
									pointerEvents: "auto",
									display: "block",
								}
							: {},
					"&.Mui-focused .MuiAutocomplete-tag, &:hover .MuiAutocomplete-tag":
						Array.isArray(value) && value.length
							? {
									flex: "0 0 60%",
									maxWidth: "60%",
									display: "flex",
								}
							: {},
				}}
			/>
		</Box>
	);
};

export default SearchInputFieldWithDebounce;
